<template>
    <div
        ref="root"
        v-intersect="handleVisibilityChange"
        class="virtual-scroller"
        :class="{
            ready,
            'page-mode': pageMode,
            [`direction-${direction}`]: true,
        }"
        @scroll.passive="handleScroll">
        <component
            :is="listTag"
            ref="wrapper"
            :style="{ [direction === 'vertical' ? 'minHeight' : 'minWidth']: totalSizeRef + 'px' }"
            class="virtual-scroller__item-list"
            :class="listClass">
            <component
                :is="itemTag"
                v-for="view of poolArray"
                :key="view.nr.id"
                :style="computeStyle(view)"
                class="virtual-scroller__item-view">
                <slot
                    :item="view.item"
                    :index="view.nr.index"
                    :active="view.nr.used"/>
            </component>
            <slot name="empty"/>
        </component>
    </div>
</template>
<script setup lang="ts">
import getScrollParent from '../utilities/getScrollParent';
import {
    computed,
    markRaw,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    onServerPrefetch,
    ref,
    shallowReactive,
    watch,
} from 'vue';

export interface VirtualScrollerProps {
    items: never[],
    direction?: 'horizontal' | 'vertical',
    itemsLimit?: number
    itemSize?: number,
    dynamicSize?: boolean,
    minItemSize?: number | string,
    defaultItemSize?: number | string,
    keyField?: string,
    sizeField?: string,
    typeField?: string,
    buffer?: number,
    pageMode?: boolean,
    prerender?: number,
    emitUpdate?: boolean,
    updateInterval?: number,
    listTag?: string,
    itemTag?: string,
    listClass?: string | Record<string, unknown> | Record<string, unknown>[],
    itemClass?: string | Record<string, unknown> | Record<string, unknown>[],
}

const props = withDefaults(defineProps<VirtualScrollerProps>(), {
    direction: 'vertical',
    itemsLimit: 1000,
    itemSize: null,
    dynamicSize: true,
    minItemSize: null,
    defaultItemSize: null,
    keyField: 'id',
    sizeField: 'size',
    typeField: 'type',
    buffer: 0,
    pageMode: false,
    prerender: 0,
    emitUpdate: false,
    updateInterval: 0,
    listTag: 'div',
    itemTag: 'div',
    listClass: '',
    itemClass: '',
});

// const gridRows = <T, >(array: T[], columns: number): T[][] => {
//     return Array.from(
//         new Array(Math.ceil(array.length / columns)),
//         (_, i) => array.slice(i * columns, i * columns + columns),
//     );
// };
//
// const rows = (a: number, b: number) => ~~(a / b) + +!!(a % b);

const emit = defineEmits<{
    (event: 'update', startIndex: number, endIndex: number, visibleStartIndex: number, visibleEndIndex: number): void,
    (event: 'resize'): void,
    (event: 'visible'): void,
    (event: 'hidden'): void,
    (event: 'scroll-start'): void,
    (event: 'scroll-end'): void,
}>();

interface ScrollerState {
    uid: number,
    startIndex: number,
    endIndex: number,
    views: Map,
    unusedViews: Map,
    scrollDirty: boolean,
    lastUpdateScrollPosition: number,
    prerender: boolean,
    minItemSize: number | string,
    sortTimer: number | ReturnType<typeof setTimeout>,
    updateTimeout: number | ReturnType<typeof setTimeout>,
    refreshTimeout: number | ReturnType<typeof setTimeout>,
}

const state: ScrollerState = {
    uid: 0,
    startIndex: 0,
    endIndex: 0,
    views: (new Map),
    unusedViews: (new Map),
    scrollDirty: false,
    lastUpdateScrollPosition: 0,
    prerender: false,
    minItemSize: props.minItemSize,
    sortTimer: 0,
    updateTimeout: 0,
    refreshTimeout: 0,
};

const root = ref<HTMLDivElement | null>(null);

const poolArray = ref([]);
const totalSizeRef = ref(0);
const ready = ref(false);

type SizeAccumulator = { accumulator: number, size: number };

const calculateSizes = (): SizeAccumulator[] => {
    if(props.itemSize === null) {
        const sizes: SizeAccumulator[] = [];
        const items = props.items;
        const field = props.sizeField;
        let accumulator = 0;
        for (const item of items) {
            const current = +(item[field] || props.defaultItemSize);
            if(current < state.minItemSize) {
                state.minItemSize = current;
            }
            accumulator += current;
            sizes.push({ accumulator: accumulator, size: current });
        }
        return sizes;
    }
    return null;
};

const sizesArray = computed(calculateSizes);

const isSimpleArray = computed(() => props.items.length && typeof props.items[0] !== 'object');

const getItemIndexByKey = computed(() => {
    const result = new Map;
    for (let i = 0, l = props.items.length; i < l; i++) {
        result.set(props.items[i][props.keyField], i);
    }
    return result;
});

const addView = (pool: Record<string, unknown>[], index: number, item: Record<string, unknown>, key: unknown, type: unknown) => {
    const nr = markRaw({
        id: state.uid++,
        index,
        used: true,
        key,
        type,
    });
    const view = shallowReactive({
        item,
        position: 0,
        nr,
    });
    pool.push(view);
    return view;
};

const unuseView = (view: { nr: Record<string, unknown>, position: number }, fake = false) => {
    const unusedViews = state.unusedViews;
    const type = view.nr.type;
    let unusedPool = unusedViews.get(type);
    if(!unusedPool) {
        unusedPool = [];
        unusedViews.set(type, unusedPool);
    }
    unusedPool.push(view);
    if(!fake) {
        view.nr.used = false;
        view.position = -9999;
    }
};

const handleResize = () => {
    if(ready.value) {
        return;
    }
    updateVisibleItems(false);
};

const handleScroll = () => {
    if(state.scrollDirty) {
        return;
    }
    state.scrollDirty = true;
    if(state.updateTimeout) {
        return;
    }

    const requestUpdate = () => requestAnimationFrame(() => {
        state.scrollDirty = false;
        const continuous = updateVisibleItems(false, true);


        if(!continuous) {
            clearTimeout(state.refreshTimeout);
            state.refreshTimeout = setTimeout(handleScroll, props.updateInterval + 100);
        }
    });

    requestUpdate();

    if(props.updateInterval) {
        state.updateTimeout = setTimeout(() => {
            state.updateTimeout = 0;
            if(state.scrollDirty) {
                requestUpdate();
            }
        }, props.updateInterval);
    }
};

const handleVisibilityChange = (isVisible: boolean, entry: HTMLElement) => {
    if(ready.value) {
        if(isVisible || entry.getBoundingClientRect().width !== 0 || entry.getBoundingClientRect().height !== 0) {
            emit('visible');
            requestAnimationFrame(() => {
                updateVisibleItems(false);
            });
        } else {
            emit('hidden');
        }
    }
};

const getScroll = () => {
    const isVertical = props.direction === 'vertical';
    let scrollState;

    if(props.pageMode) {
        const bounds = root.value.getBoundingClientRect();
        const boundsSize = isVertical ? bounds.height : bounds.width;
        let start = -(isVertical ? bounds.top : bounds.left);
        let size = isVertical ? window.innerHeight : window.innerWidth;
        if(start < 0) {
            size += start;
            start = 0;
        }
        if(start + size > boundsSize) {
            size = boundsSize - start;
        }
        scrollState = {
            start,
            end: start + size,
        };
    } else if(isVertical) {
        scrollState = {
            start: root.value.scrollTop,
            end: root.value.scrollTop + root.value.clientHeight,
        };
    } else {
        scrollState = {
            start: root.value.scrollLeft,
            end: root.value.scrollLeft + root.value.clientWidth,
        };
    }

    return scrollState;
};

const itemsLimitError = () => {
    setTimeout(() => {
        console.warn('It seems the scroller element isn\'t scrolling, so it tries to render all the items at once.', 'Scroller:', root.value);
        console.warn('Make sure the scroller has a fixed height (or width) and \'overflow-y\' (or \'overflow-x\') set to \'auto\' so it can scroll correctly and only render the items visible in the scroll viewport.');
    });
    throw new Error('Rendered items limit reached');
};

const sortViews = () => {
    poolArray.value.sort((viewA, viewB) => viewA.nr.index - viewB.nr.index);
};

function updateVisibleItems(checkItem: boolean, checkPositionDiff = false) {
    const itemSize = props.itemSize;
    const minItemSize = state.minItemSize;
    const typeField = props.typeField;
    const keyField = isSimpleArray.value ? null : props.keyField;
    const items = props.items;
    const count = items.length;
    const sizes = sizesArray.value;
    const views = state.views;
    const unusedViews = state.unusedViews;
    const pool = poolArray.value;
    const itemIndexByKey = getItemIndexByKey.value;
    let startIndex, endIndex;
    let totalSize;
    let visibleStartIndex, visibleEndIndex;

    if(!count) {
        startIndex = endIndex = visibleStartIndex = visibleEndIndex = totalSize = 0;
    } else if(state.prerender) {
        startIndex = visibleStartIndex = 0;
        endIndex = visibleEndIndex = Math.min(props.prerender, items.length);
        totalSize = null;
    } else {
        const scroll = getScroll();

        if(checkPositionDiff) {
            let positionDiff = scroll.start - state.lastUpdateScrollPosition;
            if(positionDiff < 0) positionDiff = -positionDiff;
            if((itemSize === null && positionDiff < minItemSize) || positionDiff < itemSize) {
                return true;
            }
        }
        state.lastUpdateScrollPosition = scroll.start;

        const buffer = props.buffer;
        scroll.start -= buffer;
        scroll.end += buffer;

        if(itemSize === null) {
            let h;
            let a = 0;
            let b = count - 1;
            let i = ~~(count / 2);
            let previous;

            do {
                previous = i;
                h = sizes[i].accumulator;
                if(h < scroll.start) {
                    a = i;
                } else if(i < count - 1 && sizes[i + 1].accumulator > scroll.start) {
                    b = i;
                }
                i = ~~((a + b) / 2);
            } while (i !== previous);
            
            if(i < 0) {
                i = 0;
            }
            startIndex = i;

            totalSize = sizes[count - 1].accumulator;

            endIndex = i;
            while(endIndex < count && sizes[endIndex].accumulator < scroll.end) {
                endIndex++;
            }

            if(endIndex === -1) {
                endIndex = items.length - 1;
            } else {
                endIndex++;
                endIndex > count && (endIndex = count);
            }

            visibleStartIndex = startIndex;
            while(visibleStartIndex < count && (sizes[visibleStartIndex].accumulator) < scroll.start) {
                visibleStartIndex++;
            }

            visibleEndIndex = visibleStartIndex;
            while(visibleEndIndex < count && (sizes[visibleEndIndex].accumulator) < scroll.end) {
                visibleEndIndex++;
            }

        } else {
            startIndex = ~~(scroll.start / itemSize);
            endIndex = Math.ceil(scroll.end / itemSize);
            visibleStartIndex = Math.max(0, Math.floor(scroll.start / itemSize));
            visibleEndIndex = Math.floor(scroll.end / itemSize);

            if(startIndex < 0) {
                startIndex = 0;
            }
            if(endIndex > count) {
                endIndex = count;
            }
            if(visibleStartIndex < 0) {
                visibleStartIndex = 0;
            }
            if(visibleEndIndex > count) {
                visibleEndIndex = count;
            }

            totalSize = count * itemSize;
        }
    }

    if(endIndex - startIndex > props.itemsLimit) {
        itemsLimitError();
    }

    totalSizeRef.value = totalSize;

    let view;

    const continuous = startIndex <= state.endIndex && endIndex >= state.startIndex;

    if(continuous) {
        for (let i = 0, l = pool.length; i < l; i++) {
            view = pool[i];
            if(!view.nr.used) {
                continue;
            }
            if(checkItem) {
                view.nr.index = itemIndexByKey.get(view.item[keyField]);
            }
            if(view.nr.index === null || view.nr.index < startIndex || view.nr.index >= endIndex) {
                unuseView(view);
            }
        }
    }

    const unusedIndex = continuous ? null : (new Map);

    let item, type;
    let v;
    for (let i = startIndex; i < endIndex; i++) {
        item = items[i];
        const key = keyField ? item[keyField] : item;
        if(key === null) {
            throw new Error(`Key is ${key} on item (keyField is '${keyField}')`);
        }
        view = views.get(key);

        if(!itemSize && !sizes[i].size) {
            if(view) unuseView(view);
            continue;
        }

        type = item[typeField];

        let unusedPool = unusedViews.get(type);
        let newlyUsedView = false;

        if(!view) {
            if(continuous) {
                if(unusedPool && unusedPool.length) {
                    view = unusedPool.pop();
                } else {
                    view = addView(pool, i, item, key, type);
                }
            } else {
                v = unusedIndex.get(type) || 0;

                if(!unusedPool || v >= unusedPool.length) {
                    view = addView(pool, i, item, key, type);
                    unuseView(view, true);
                    unusedPool = unusedViews.get(type);
                }

                view = unusedPool[v];
                unusedIndex.set(type, v + 1);
            }

            views.delete(view.nr.key);
            view.nr.used = true;
            view.nr.index = i;
            view.nr.key = key;
            view.nr.type = type;
            views.set(key, view);

            newlyUsedView = true;
        } else {
            if(!view.nr.used) {
                view.nr.used = true;
                newlyUsedView = true;
                if(unusedPool) {
                    const index = unusedPool.indexOf(view);
                    if(index !== -1) unusedPool.splice(index, 1);
                }
            }
        }

        view.item = item;

        if(newlyUsedView) {
            if(i === items.length - 1) {
                emit('scroll-end');
            }
            if(i === 0) {
                emit('scroll-start');
            }
        }

        if(itemSize === null) {
            view.position = i > 0 ? sizes[i - 1].accumulator : 0;
            view.offset = 0;
        } else {
            view.position = i * itemSize;
            view.offset = 0;
        }
    }

    state.startIndex = startIndex;
    state.endIndex = endIndex;

    if(props.emitUpdate) emit('update', startIndex, endIndex, visibleStartIndex, visibleEndIndex);

    clearTimeout(state.sortTimer);
    state.sortTimer = setTimeout(sortViews, props.updateInterval + 300);

    return continuous;
}

const getListenerTarget = () => {
    const target = getScrollParent(root.value);
    if(window.document && (target === window.document.documentElement || target === window.document.body)) {
        return window;
    }
    return target;
};

const addListeners = () => {
    const listenerTarget = getListenerTarget();
    listenerTarget.addEventListener('scroll', handleScroll, { passive: true });
    listenerTarget.addEventListener('resize', handleResize);
};

const removeListeners = () => {
    const listenerTarget = getListenerTarget();

    if(!listenerTarget) {
        return;
    }

    listenerTarget.removeEventListener('scroll', handleScroll);
    listenerTarget.removeEventListener('resize', handleResize);
};

const applyPageMode = () => {
    if(props.pageMode) {
        addListeners();
    } else {
        removeListeners();
    }
};

const computeStyle = (view: { nr: never, position: number, offset: number}) => {
    if(ready.value) {
        return {
            transform: ''
                + `translate${props.direction === 'vertical' ? 'Y' : 'X'}(${view.position}px)`
                + `translate${props.direction === 'vertical' ? 'X' : 'Y'}(${view.offset}px)`,
        };
    }
    return null;
};

const scrollOffset = (element: Element, vertical: boolean) => {
    const scrollTop = element.tagName === 'HTML' ? 0 : vertical ? element.scrollTop : element.scrollLeft;
    const bounds = element?.getBoundingClientRect();
    const scroller = root.value.getBoundingClientRect();
    const scrollerPosition = (vertical ? scroller.top : scroller.left) - (vertical ? bounds.top : bounds.left);

    return scrollTop + scrollerPosition;
};

const scrollToPosition = (position: number) => {
    const vertical = props.direction === 'vertical';
    const viewport = props.pageMode ? getScrollParent(root.value) : root.value;
    const distance = props.pageMode ? scrollOffset(viewport, vertical) + position : position;
    if(vertical) {
        viewport.scrollTop = distance;
    } else {
        viewport.scrollLeft = distance;
    }
};

const scrollToItem = (index: number, stickyOffset = 0) => {
    let scroll;
    if(props.itemSize === null) {
        scroll = index > 0 ? sizesArray.value[index - 1].accumulator : 0;
    } else {
        scroll = index * props.itemSize;
    }
    scrollToPosition(scroll - stickyOffset);
};


watch(() => props.items, () => props.defaultItemSize, () => {
    if(!root.value) {
        return;
    }
    updateVisibleItems(true);
});

watch([sizesArray, () => props.defaultItemSize, () => props.pageMode], () => {
    if(!root.value) {
        return;
    }
    applyPageMode();
    updateVisibleItems(false);
}, { deep: true });

onServerPrefetch(() => {
    if(props.prerender) {
        state.prerender = true;
        updateVisibleItems(false);
    }
});

onMounted(() => {
    applyPageMode();
    nextTick(() => {
        state.prerender = false;
        updateVisibleItems(true);
        ready.value = true;
    });
});

onActivated(() => {
    const lastPosition = state.lastUpdateScrollPosition;
    if(lastPosition) {
        nextTick(() => {
            scrollToPosition(lastPosition);
        });
    }
});

onBeforeUnmount(removeListeners);

defineExpose({
    scrollToItem,
});
</script>

<style>
.virtual-scroller {
    position: relative;
}

.virtual-scroller.direction-vertical:not(.page-mode) {
    overflow-y: auto;
}

.virtual-scroller.direction-horizontal:not(.page-mode) {
    overflow-x: auto;
}

.virtual-scroller.direction-horizontal {
    display: flex;
}

.virtual-scroller__slot {
    flex: auto 0 0;
}

.virtual-scroller__item-list {
    flex: 1;
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
}

.virtual-scroller.ready .virtual-scroller__item-view {
    position: absolute;
    top: 0;
    left: 0;
    will-change: transform;
}

.virtual-scroller.direction-vertical .virtual-scroller__item-list {
    width: 100%;
}

.virtual-scroller.direction-horizontal .virtual-scroller__item-list {
    height: 100%;
}

.virtual-scroller.ready.direction-vertical .virtual-scroller__item-view {
    width: 100%;
}

.virtual-scroller.ready.direction-horizontal .virtual-scroller__item-view {
    height: 100%;
}
</style>
